import { Pencil } from 'lucide-react';

import { ConditionalRender, Icon, Inline, Typography } from '@crossnokaye/ux-common';
import {
  Header,
  HeaderIcon,
  HeaderRightActions,
  HeaderTitle,
  HeaderTitleGroup,
} from '@crossnokaye/ux-common/ckds';

import { TooltipButton } from '@/components/molecules/tooltip-button';
import { useEquipmentBulkEditAccess } from '@/features/equipment-browser/access/use-equipment-bulk-edit-access';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

interface EquipmentBulkEditActionBarProps {
  isPushOutOpen: boolean;
  onEdit: () => void;
  rowsSelected: number;
}

export const EquipmentBulkEditActionBar = ({
  isPushOutOpen,
  onEdit,
  rowsSelected,
}: EquipmentBulkEditActionBarProps) => {
  const { canUseMultiSelectEdit, multiSelectDeniedTooltip } = useEquipmentBulkEditAccess();
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({ privilege: PRIVILEGES.writeCommands });

  const canBulkEdit = rowsSelected > 0;
  const isBulkEditDisabled = !canBulkEdit || disabledByPermissions || !canUseMultiSelectEdit;

  const getBulkEditTooltip = () => {
    if (!canUseMultiSelectEdit) return multiSelectDeniedTooltip;
    if (disabledByPermissions) return permissionTooltipText;
    if (!canBulkEdit) return 'No rows selected';
    return undefined;
  };

  return (
    <Header className="min-h-[3.5rem] p-stack-sm bg-ckds-yellow-500/15">
      <HeaderIcon>
        <Icon
          icon={Pencil}
          color="white"
          backgroundColor="bg-ckds-button-bg-tier2"
          variant="rounded"
          size="md"
        />
      </HeaderIcon>
      <HeaderTitleGroup>
        <HeaderTitle>
          <Inline gap="sm">
            <Typography variant="body" className="text-ckds-text-fg-tier2 uppercase">
              {'Editing Settings'}
            </Typography>
            {canBulkEdit && (
              <span className="text-ckds-text-fg-tier3 text-sm">
                {rowsSelected + ' rows selected'}
              </span>
            )}
          </Inline>
        </HeaderTitle>
      </HeaderTitleGroup>
      <ConditionalRender condition={!isPushOutOpen}>
        <HeaderRightActions>
          <Inline gap="sm">
            <div className="w-px self-stretch bg-ckds-button-bg-tier2" />
            <TooltipButton
              variant="default"
              size="md"
              onClick={onEdit}
              disabled={isBulkEditDisabled}
              tooltipContent={getBulkEditTooltip()}
            >
              {'Bulk Edit'}
            </TooltipButton>
          </Inline>
        </HeaderRightActions>
      </ConditionalRender>
    </Header>
  );
};
